<template>
    <div class="container-fluid">
        <div class="row">
            <!-- Sidebar for Form Controls -->
            <div class="col-md-3 sidebar bg-light">
                <div class="info-panel p-3">
                    <h2 class="mb-4">Climate Stripes</h2>
                    <p class="text-muted">
                        Visualize temperature anomalies for a selected scenario and location as a series of colored stripes.
                    </p>

                    <!-- RCP Scenario Selection -->
                    <div class="form-group mt-3">
                        <label for="rcp-scenario-select">Select RCP Scenario</label>
                        <select id="rcp-scenario-select" v-model="selectedRcpScenario" class="form-control">
                            <option v-for="scenario in availableRcpScenarios" :key="scenario" :value="scenario">
                                {{ scenario }}
                            </option>
                        </select>
                    </div>

                    <!-- Date Selection -->
                    <div class="form-group mt-3">
                        <label for="start-year-select">Select Start Year</label>
                        <Datepicker
                            v-model="selectedStartYear"
                            year-picker
                            auto-apply
                            :year-range="dynamicYearRange"
                            :disabled-dates="disabledDateHandler"
                            :clearable="false"
                        />
                    </div>

                    <div class="form-group mt-3">
                        <label for="end-year-select">Select End Year</label>
                        <Datepicker
                            v-model="selectedEndYear"
                            year-picker
                            auto-apply
                            :year-range="dynamicYearRange"
                            :disabled-dates="disabledDateHandler"
                            :clearable="false"
                        />
                    </div>

                    <!-- Spatial Extent -->
                    <div class="mt-4">
                        <button class="btn btn-secondary" @click="showMapModal = true" :disabled="isBboxLoading">
                            Select Spatial Extent
                        </button>
                    </div>

                    <!-- Generate Stripes Button -->
                    <div class="mt-3">
                        <button
                            class="btn btn-primary w-100"
                            :disabled="!selectedRcpScenario || !selectedStartYear || !selectedEndYear || isLoading || isBboxLoading"
                            @click="generateClimateStripes"
                        >
                            {{ isLoading ? 'Generating...' : 'Generate Stripes' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Main Content: Climate Stripes Visualization -->
            <div class="col-md-9">
                <div v-if="isLoading" class="loading-indicator mt-2">
                    <div class="spinner"></div>
                    <p>Fetching and processing ensemble data, please wait...</p>
                </div>

                <div v-else>
                    <div class="stripes-container mt-4">
                        <h4 v-if="stripesGenerated">Climate Stripes for {{ selectedRcpScenario }}</h4>
                        <p v-if="stripesGenerated">Showing annual mean temperature anomaly from {{ displayYears.start }} to {{ displayYears.end }}.
                            <br>Baseline reference period: {{ baselineInfo }}.
                        </p>
                        <!-- Always in DOM, visibility toggled by v-show -->
                        <div
                            id="plotly-chart"
                            ref="chartContainer"
                            class="w-100"
                            v-show="stripesGenerated"
                        ></div>
                    </div>

                    <div v-if="errorMessage" class="placeholder-container">
                        <p class="placeholder-text text-danger">Error: {{ errorMessage }}</p>
                    </div>

                    <div v-if="!stripesGenerated && !errorMessage">
                        <div class="placeholder-container">
                            <p class="placeholder-text">
                                Please select parameters and generate the climate stripes.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Mapbox Map -->
        <div v-if="showMapModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Select Spatial Extent</h5>
                    <button type="button" class="btn-close" @click="closeMapModal"></button>
                </div>
                <div class="modal-body">
                    <MapboxMap :initialBBOX="initialBBOX" @update-bbox="updateBbox" />
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary" @click="closeMapModal">Done</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import MapboxMap from './MapComponent.vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import Plotly from 'plotly.js-dist-min';

export default {
    name: 'ClimateStripesTool',
    components: { MapboxMap, Datepicker },
    setup() {
        const parsedIds = ref([]);
        const initialBBOX = ref([]);
        const currentBbox = ref(null);
        const showMapModal = ref(false);
        const availableDateRange = ref({ min: 1950, max: 2100 });
        const chartContainer = ref(null);

        const selectedRcpScenario = ref(null);
        const selectedStartYear = ref(null);
        const selectedEndYear = ref(null);

        const isLoading = ref(false);
        const isBboxLoading = ref(true);
        const stripesGenerated = ref(false);
        const errorMessage = ref('');
        const displayYears = ref({});
        const baselineInfo = ref('1961-2020 (from observational data)');

        const OBSERVATION_DATASET_ID = "Observation_Data_MDK_Region_Temperature_mean";

        const fetchInitialBbox = async (collectionId) => {
            isBboxLoading.value = true;
            try {
                const response = await fetch(`/api/collections/${collectionId}`);
                const schema = await response.json();
                if (schema.extent?.spatial?.bbox?.[0]) {
                    initialBBOX.value = schema.extent.spatial.bbox[0];
                }
            } catch (error) {
                console.error('Error fetching initial BBOX:', error);
                errorMessage.value = "Failed to load initial map extent.";
            } finally {
                isBboxLoading.value = false;
            }
        };

        const fetchAndProcessIds = async () => {
            try {
                const response = await fetch('/api/collections');
                const data = await response.json();
                if (data.collections && data.collections.length > 0) {
                    parsedIds.value = data.collections
                        .map(c => parseId(c.id))
                        .filter(id => id !== null);

                    const referenceCollection = data.collections.find(c => c.id.startsWith('MDK_SN_SA_TH'));

                    if (referenceCollection) {
                        await fetchInitialBbox(referenceCollection.id);
                    } else {
                        console.error('No collection starting with "MDK_SN_SA_TH" found to determine initial BBOX.');
                        errorMessage.value = "Could not determine initial map extent.";
                    }

                    if (availableRcpScenarios.value.length > 0) {
                        selectedRcpScenario.value = availableRcpScenarios.value[0];
                    }
                }
            } catch (error) {
                errorMessage.value = "Failed to fetch model data.";
                console.error('Error fetching or processing IDs:', error);
            }
        };

        const parseId = (id) => {
            // Exclude IDs with "KlimaKonform" or not starting with the expected prefix
            if (id.includes('KlimaKonform') || !id.startsWith('MDK_SN_SA_TH_')) {
                return null;
            }

            const parts = id.split('_');
            // Find the RCP scenario part, which can be r26, r45, or r85
            const rcpScenario = parts.find(p => ['r26', 'r45', 'r85'].includes(p));

            // Basic validation to ensure we have a model and scenario
            if (rcpScenario && parts.length >= 8) {
                const globalModel = parts[5];
                const regionalModel = parts[6];
                return { id, globalModel, regionalModel, rcpScenario };
            }

            return null;
        };

        // --- Computed ---
        const availableRcpScenarios = computed(() =>
            [...new Set(parsedIds.value.map(id => id.rcpScenario))].sort()
        );

        const selectedIDs = computed(() => {
            if (!selectedRcpScenario.value) return [];
            return parsedIds.value
                .filter(id => id.rcpScenario === selectedRcpScenario.value)
                .map(id => id.id);
        });

        const dynamicYearRange = computed(() => [availableDateRange.value.min, availableDateRange.value.max]);

        // --- Watchers ---
        watch(selectedIDs, async (newIds) => {
            if (newIds.length === 0) return;

            const period = await validateTimeCoverage(newIds);
            if (period) {
                const minYear = period.minDate.getFullYear();
                const maxYear = period.maxDate.getFullYear();
                availableDateRange.value = { min: minYear, max: maxYear };
                selectedStartYear.value = minYear;
                selectedEndYear.value = maxYear;
            } else {
                errorMessage.value = "Could not determine a common time range for the selected models.";
            }
        });

        // --- Core Methods ---
        const getYear = (date) => {
            if (date instanceof Date) {
                return date.getFullYear();
            }
            return date;
        };

        const fetchClimateDataEnsemble = async (collectionIds, startYear, endYear, bbox) => {
            try {
                const response = await fetch('/api/explorer-data', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        collection_ids: collectionIds,
                        climate_indicator: 'temperature_mean',
                        start_year: startYear.toString(),
                        end_year: endYear.toString(),
                        bbox: bbox,
                        temporal_resolution: 'ANNUAL'
                    })
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || 'Failed to fetch climate data');
                }

                return await response.json();
            } catch (error) {
                console.error('Error fetching climate data:', error);
                throw error;
            }
        };

        const fetchObservationalBaseline = async (bbox) => {
            try {
                console.log("[DEBUG] Fetching baseline with collection_ids:", [OBSERVATION_DATASET_ID]);
                
                // Use the /api/fetch-baseline endpoint with the observational dataset ID
                const response = await fetch('/api/fetch-baseline', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        collection_ids: [OBSERVATION_DATASET_ID],
                        bbox: bbox
                    })
                });

                if (!response.ok) {
                    console.warn('Observational baseline not available, using ensemble mean as reference');
                    return null;
                }

                const responseData = await response.json();
                const baselineMean = responseData.baselineMean;
                
                if (baselineMean !== null && !isNaN(baselineMean)) {
                    console.log("[DEBUG] Baseline mean fetched successfully:", baselineMean);
                    return baselineMean;
                }

                console.warn('No valid baseline data available');
                return null;
            } catch (error) {
                console.error('Error fetching observational baseline:', error);
                return null;
            }
        };

        const generateClimateStripes = async () => {
            if (!selectedRcpScenario.value || !selectedStartYear.value || !selectedEndYear.value) return;

            isLoading.value = true;
            stripesGenerated.value = false;
            errorMessage.value = '';

            try {
                const bbox = currentBbox.value || initialBBOX.value;
                const startYear = getYear(selectedStartYear.value);
                const endYear = getYear(selectedEndYear.value);

                console.log("[DEBUG] Start year:", startYear, "End year:", endYear);
                console.log("[DEBUG] Selected IDs:", selectedIDs.value);

                // Fetch observational baseline using the dedicated endpoint
                console.log("[DEBUG] Fetching observational baseline...");
                let baselineMean = await fetchObservationalBaseline(bbox);
                console.log("[DEBUG] Baseline mean:", baselineMean);

                // Fetch ensemble data for the selected RCP scenario
                console.log("[DEBUG] Fetching ensemble data for", selectedRcpScenario.value);
                const ensembleData = await fetchClimateDataEnsemble(
                    selectedIDs.value,
                    startYear,
                    endYear,
                    bbox
                );

                if (!ensembleData || !ensembleData.value) {
                    throw new Error("No ensemble data received from backend");
                }

                console.log("[DEBUG] Ensemble data structure:", ensembleData);

                // Organize data by year across all models
                const yearlyData = {};
                const numYears = endYear - startYear + 1;
                
                for (let year = startYear; year <= endYear; year++) {
                    yearlyData[year] = [];
                }

                // Collect all values for each year from all models
                for (const model in ensembleData.value) {
                    const modelData = ensembleData.value[model];
                    if (modelData.values && Array.isArray(modelData.values)) {
                        const validValues = modelData.values.filter(v => v !== null && !isNaN(v));
                        
                        // Assume values are in chronological order matching startYear to endYear
                        for (let i = 0; i < validValues.length && i < numYears; i++) {
                            const year = startYear + i;
                            yearlyData[year].push(validValues[i]);
                        }
                    }
                }

                // Calculate annual ensemble means
                const annualMeans = [];
                for (let year = startYear; year <= endYear; year++) {
                    const yearValues = yearlyData[year];
                    if (yearValues.length > 0) {
                        const yearMean = yearValues.reduce((sum, val) => sum + val, 0) / yearValues.length;
                        annualMeans.push(yearMean);
                        console.log(`[DEBUG] Year ${year}: ${yearValues.length} values, mean = ${yearMean.toFixed(2)}`);
                    } else {
                        annualMeans.push(null);
                        console.log(`[DEBUG] Year ${year}: no data`);
                    }
                }

                console.log("[DEBUG] Annual means:", annualMeans);

                // If no baseline available, use ensemble mean as reference
                if (baselineMean === null) {
                    const allAnnualMeans = annualMeans.filter(v => v !== null);
                    if (allAnnualMeans.length > 0) {
                        baselineMean = allAnnualMeans.reduce((sum, val) => sum + val, 0) / allAnnualMeans.length;
                        baselineInfo.value = `${startYear}-${endYear} (ensemble mean)`;
                        console.log("[DEBUG] Using ensemble mean as baseline:", baselineMean);
                    } else {
                        throw new Error("No valid data to calculate baseline");
                    }
                } else {
                    baselineInfo.value = '1961-2020 (from observational data)';
                }

                isLoading.value = false;
                stripesGenerated.value = true;
                await nextTick();
                await renderPlotlyChart(annualMeans, baselineMean, startYear, endYear);

            } catch (error) {
                console.error('Error generating climate stripes:', error);
                errorMessage.value = `Failed to generate stripes: ${error.message}`;
                isLoading.value = false;
                stripesGenerated.value = false;
            }
        };

        const renderPlotlyChart = async (series, baselineMean, startYear, endYear) => {
            if (!series || series.length === 0) {
                errorMessage.value = "No data available to render the chart.";
                return;
            }

            displayYears.value = { start: startYear, end: endYear };
            const anomalies = series.map(val => (val !== null && !isNaN(val) ? val - baselineMean : null));
            const years = Array.from({ length: series.length }, (_, i) => startYear + i);
            const validAnomalies = anomalies.filter(a => a !== null && !isNaN(a));
            const maxAbsAnomaly = validAnomalies.length > 0 ? Math.max(...validAnomalies.map(Math.abs)) : 1;

            console.log("[DEBUG] Anomalies:", anomalies);
            console.log("[DEBUG] Max absolute anomaly:", maxAbsAnomaly);

            const data = [
                {
                    x: years,
                    y: [''],
                    z: [anomalies],
                    type: 'heatmap',
                    colorscale: [
                        ['0.0', 'rgb(0,0,255)'],
                        ['0.5', 'rgb(255,255,255)'],
                        ['1.0', 'rgb(255,0,0)']
                    ],
                    zmin: -maxAbsAnomaly,
                    zmax: maxAbsAnomaly,
                    showscale: true,
                    colorbar: { title: 'Anomaly (°C)', titleside: 'right' }
                }
            ];

            const layout = {
                title: `Climate Stripes for ${selectedRcpScenario.value} (${startYear}-${endYear})`,
                xaxis: { title: 'Year', dtick: 10 },
                yaxis: { showticklabels: false },
                autosize: true,
                margin: { l: 50, r: 50, b: 100, t: 100, pad: 4 }
            };

            const config = { responsive: true, displaylogo: false };
            await nextTick();
            if (chartContainer.value) {
                Plotly.newPlot(chartContainer.value, data, layout, config);
            } else {
                console.error("Plotly chart container not found.");
            }
        };

        // --- Helpers ---
        const disabledDateHandler = (date) => {
            const year = date.getFullYear();
            return year < availableDateRange.value.min || year > availableDateRange.value.max;
        };

        const validateTimeCoverage = async (ids) => {
            if (!ids || ids.length === 0) return null;
            const baseUrl = '/api/collections/';
            try {
                const results = await Promise.all(ids.map(id => fetch(`${baseUrl}${id}?f=json`).then(res => res.json())));
                const intervals = results.map(result => result.extent.temporal.interval[0]);
                let intersectionStart = new Date(intervals[0][0]);
                let intersectionEnd = new Date(intervals[0][1]);
                intervals.forEach(interval => {
                    const start = new Date(interval[0]);
                    const end = new Date(interval[1]);
                    if (start > intersectionStart) intersectionStart = start;
                    if (end < intersectionEnd) intersectionEnd = end;
                });
                return intersectionStart < intersectionEnd ? { minDate: intersectionStart, maxDate: intersectionEnd } : null;
            } catch (error) {
                console.error('Error validating time coverage:', error);
                return null;
            }
        };

        const updateBbox = (bbox) => (currentBbox.value = bbox);
        const closeMapModal = () => (showMapModal.value = false);

        onMounted(() => {
            fetchAndProcessIds();
        });

        return {
            initialBBOX,
            availableRcpScenarios,
            dynamicYearRange,
            chartContainer,
            selectedRcpScenario,
            selectedStartYear,
            selectedEndYear,
            isLoading,
            isBboxLoading,
            stripesGenerated,
            errorMessage,
            displayYears,
            baselineInfo,
            showMapModal,
            generateClimateStripes,
            updateBbox,
            closeMapModal,
            disabledDateHandler
        };
    }
};
</script>

<style scoped>
.sidebar {
    border-radius: 15px;
}
.info-panel {
    border-radius: 15px;
}
.loading-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    min-height: 300px;
}
.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
.modal-overlay {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}
.modal-content {
    background-color: white;
    padding: 20px;
    width: 80%;
    max-width: 800px;
    border-radius: 8px;
}
.placeholder-container,
.stripes-container {
    text-align: center;
    padding: 50px;
    border: 1px dashed #ccc;
    border-radius: 10px;
    margin-top: 20px;
    min-height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}
.placeholder-text {
    color: #888;
    font-style: italic;
}
.canvas-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}
canvas {
    border: 1px solid #dee2e6;
    border-radius: 5px;
    height: 200px;
    max-width: 100%;
}
</style>
